import { useKeydownGlobal } from '@/hooks/useKeydownGlobal'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@ttab/elephant-ui'
import { LoaderIcon } from '@ttab/elephant-ui/icons'
import type { MouseEvent, PropsWithChildren, JSX } from 'react'
import { useRef, useState } from 'react'

export const CreatePrompt = ({
  title,
  description,
  secondaryDescription,
  primaryLabel,
  secondaryLabel,
  onPrimary,
  onSecondary,
  children
}: {
  title?: string
  description: string
  secondaryDescription?: string
  primaryLabel: string
  secondaryLabel?: string
  onPrimary: () => void
  onSecondary?: (event: MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement> | KeyboardEvent) => void
  planningTitle?: string
} & PropsWithChildren): JSX.Element => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)

  const handleSubmit = () => {
    if (isSubmittingRef.current) return

    isSubmittingRef.current = true
    setIsSubmitting(true)
    onPrimary()
  }

  useKeydownGlobal((event) => {
    if (event.key === 'Escape' && secondaryLabel && onSecondary) {
      onSecondary(event)
    }
  })

  return (
    <Dialog open={true}>
      <DialogContent
        initialFocus={false}
      >
        <DialogHeader>
          {!!title && <DialogTitle>{title}</DialogTitle>}
        </DialogHeader>

        <DialogDescription>
          {description}
        </DialogDescription>

        {secondaryDescription && (
          <DialogDescription>
            {secondaryDescription}
          </DialogDescription>
        )}

        {children}
        <DialogFooter className='flex flex-col gap-2 pt-4'>
          {!!onSecondary && !!secondaryLabel && (
            <Button
              variant='secondary'
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                onSecondary(event)
              }}
            >
              {secondaryLabel}
            </Button>
          )}

          <Button
            autoFocus
            onClick={handleSubmit}
            disabled={isSubmitting}
            onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
              if (event.key === 'Enter') {
                handleSubmit()
              }
            }}
          >
            {isSubmitting
              ? <LoaderIcon size={14} strokeWidth={1.75} className='animate-spin' />
              : primaryLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
